import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import { 
  FileText, Download, TrendingUp, Award, Users, DollarSign, Heart,
  Calendar, Clock, Target, Loader2, RefreshCw, ChevronDown, CheckCircle
} from 'lucide-react';
import { COLORS, CHART_COLORS } from '../../../constants/dashboard';
import ProgressRing from '../shared/ProgressRing';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ReportsView = () => {
  const [loading, setLoading] = useState(true);
  const [activeReport, setActiveReport] = useState('esg');
  const [esgData, setEsgData] = useState(null);
  const [taxData, setTaxData] = useState(null);
  const [impactData, setImpactData] = useState(null);
  const [matchingData, setMatchingData] = useState(null);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedCharity, setSelectedCharity] = useState(null);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('check');
  const [processingPayment, setProcessingPayment] = useState(false);
  const [showTaxDocument, setShowTaxDocument] = useState(false);
  const [quarters, setQuarters] = useState([]);
  const [selectedQuarter, setSelectedQuarter] = useState('');
  const [downloadingPdf, setDownloadingPdf] = useState(false);

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/reports/board-quarters`, { withCredentials: true })
      .then(r => {
        const list = r.data.quarters || [];
        setQuarters(list);
        if (list.length) setSelectedQuarter(list[0].quarter);
      })
      .catch(() => {});
  }, []);

  const downloadBoardReport = async () => {
    if (!selectedQuarter) return;
    setDownloadingPdf(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/reports/board-pdf?quarter=${selectedQuarter}`, {
        withCredentials: true,
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `board-report-${selectedQuarter}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      console.error('Board report download failed', e);
    } finally {
      setDownloadingPdf(false);
    }
  };

  useEffect(() => {
    fetchReports();
  }, []);

  const fetchReports = async () => {
    setLoading(true);
    try {
      const [esg, tax, impact, matching] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/reports/esg`, { withCredentials: true }).catch(() => ({ data: null })),
        axios.get(`${BACKEND_URL}/api/reports/tax-receipts?year=${selectedYear}`, { withCredentials: true }).catch(() => ({ data: null })),
        axios.get(`${BACKEND_URL}/api/reports/impact`, { withCredentials: true }).catch(() => ({ data: null })),
        axios.get(`${BACKEND_URL}/api/donations/matching-report`, { withCredentials: true }).catch(() => ({ data: null }))
      ]);
      setEsgData(esg.data);
      setTaxData(tax.data);
      setImpactData(impact.data);
      setMatchingData(matching.data);
    } catch (err) {
      console.error('Failed to fetch reports:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleRecordPayment = async () => {
    if (!selectedCharity || !paymentAmount || parseFloat(paymentAmount) <= 0) return;
    
    setProcessingPayment(true);
    try {
      await axios.post(`${BACKEND_URL}/api/donations/matching-payment`, {
        charity_name: selectedCharity.charity_name,
        charity_ein: selectedCharity.charity_ein,
        amount: parseFloat(paymentAmount),
        payment_method: paymentMethod
      }, { withCredentials: true });
      
      setShowPaymentModal(false);
      setSelectedCharity(null);
      setPaymentAmount('');
      fetchReports(); // Refresh data
    } catch (err) {
      console.error('Payment recording failed:', err);
      alert('Failed to record payment');
    } finally {
      setProcessingPayment(false);
    }
  };

  const exportReport = async (type) => {
    window.open(`${BACKEND_URL}/api/reports/export/${type}`, '_blank');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="p-4" style={{ background: COLORS.background }}>
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-xl font-bold" style={{ color: COLORS.primary }}>Reports & Analytics</h2>
          <p className="text-sm text-gray-500">Generate ESG reports, tax receipts, and impact summaries</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={fetchReports} className="p-2 bg-white rounded-lg border shadow-sm hover:bg-gray-50">
            <RefreshCw className="w-4 h-4 text-gray-500" />
          </button>
          <select 
            value={selectedYear}
            onChange={(e) => setSelectedYear(parseInt(e.target.value))}
            className="px-3 py-2 bg-white border rounded-lg text-sm"
          >
            <option value={2025}>2025</option>
            <option value={2024}>2024</option>
            <option value={2023}>2023</option>
          </select>
        </div>
      </div>

      {/* Board Report PDF */}
      <div className="bg-white rounded-xl border shadow-sm p-5 mb-4 flex flex-wrap items-center justify-between gap-4" data-testid="board-report-card">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg bg-blue-50 border border-blue-100 flex items-center justify-center flex-shrink-0">
            <FileText className="w-5 h-5 text-blue-600" />
          </div>
          <div>
            <p className="font-bold text-gray-900">Quarterly board report</p>
            <p className="text-sm text-gray-500">
              One polished page: giving, matching, participation, charities, campaigns and a board-ready summary.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={selectedQuarter}
            onChange={(e) => setSelectedQuarter(e.target.value)}
            className="px-3 py-2.5 bg-white border rounded-lg text-sm min-w-[140px]"
            data-testid="board-quarter-select"
          >
            {quarters.length === 0 && <option value="">No activity yet</option>}
            {quarters.map(q => (
              <option key={q.quarter} value={q.quarter}>
                {q.quarter.replace('-', ' ')} · {q.donations} gifts
              </option>
            ))}
          </select>
          <button
            onClick={downloadBoardReport}
            disabled={!selectedQuarter || downloadingPdf}
            className="flex items-center gap-2 bg-gray-900 hover:bg-gray-800 disabled:opacity-50 text-white font-semibold px-5 py-2.5 rounded-lg text-sm transition-colors"
            data-testid="download-board-pdf-btn"
          >
            {downloadingPdf ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
            Download PDF
          </button>
        </div>
      </div>

      {/* Report Type Tabs */}
      <div className="flex gap-2 mb-4">
        {[
          { id: 'esg', label: 'ESG Report', icon: Award },
          { id: 'tax', label: 'Tax Receipts', icon: FileText },
          { id: 'impact', label: 'Impact Report', icon: Heart },
          { id: 'matching', label: 'Matching Report', icon: DollarSign }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveReport(tab.id)}
            className={`px-4 py-2 rounded-lg font-medium text-sm flex items-center gap-2 transition-all ${
              activeReport === tab.id
                ? 'text-white shadow-md'
                : 'bg-white text-gray-600 border hover:bg-gray-50'
            }`}
            style={activeReport === tab.id ? { background: COLORS.primary } : {}}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* ESG Report */}
      {activeReport === 'esg' && esgData && (
        <div className="space-y-4">
          {/* ESG Score Card */}
          <div className="bg-white rounded-xl shadow-sm border p-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="text-lg font-bold" style={{ color: COLORS.accent }}>ESG Performance Score</h3>
                <p className="text-sm text-gray-500">Environmental, Social & Governance metrics</p>
              </div>
              <button
                onClick={() => exportReport('donations')}
                className="px-4 py-2 text-sm font-medium rounded-lg flex items-center gap-2"
                style={{ background: `${COLORS.primary}15`, color: COLORS.primary }}
              >
                <Download className="w-4 h-4" /> Export CSV
              </button>
            </div>

            <div className="grid grid-cols-12 gap-6">
              {/* Score Gauge */}
              <div className="col-span-4 flex flex-col items-center justify-center">
                <div className="relative">
                  <ProgressRing 
                    progress={esgData.esg_score} 
                    size={160} 
                    strokeWidth={12} 
                    color={esgData.esg_score >= 80 ? '#38BDF8' : esgData.esg_score >= 60 ? '#F59E0B' : '#EF4444'} 
                  />
                  <div className="absolute inset-0 flex flex-col items-center justify-center">
                    <span className="text-4xl font-bold" style={{ color: COLORS.accent }}>{esgData.esg_score}</span>
                    <span className="text-lg font-semibold text-gray-500">/ 100</span>
                  </div>
                </div>
                <div className="mt-4 text-center">
                  <span className={`px-4 py-1 rounded-full text-lg font-bold ${
                    esgData.esg_grade.startsWith('A') ? 'bg-green-100 text-green-700' :
                    esgData.esg_grade.startsWith('B') ? 'bg-blue-100 text-blue-700' :
                    'bg-amber-100 text-amber-700'
                  }`}>
                    Grade: {esgData.esg_grade}
                  </span>
                </div>
              </div>

              {/* Score Breakdown */}
              <div className="col-span-4 space-y-3">
                <h4 className="font-semibold text-gray-700 mb-2">Score Breakdown</h4>
                {[
                  { label: 'Donation Impact', value: esgData.score_breakdown.donation_impact, max: 30, color: COLORS.primary },
                  { label: 'Employee Engagement', value: esgData.score_breakdown.employee_engagement, max: 25, color: 'var(--success-text)' },
                  { label: 'Volunteer Contribution', value: esgData.score_breakdown.volunteer_contribution, max: 25, color: '#8B5CF6' },
                  { label: 'Cause Diversity', value: esgData.score_breakdown.cause_diversity, max: 20, color: COLORS.secondary }
                ].map(item => (
                  <div key={item.label}>
                    <div className="flex justify-between text-xs mb-1">
                      <span className="text-gray-600">{item.label}</span>
                      <span className="font-medium">{item.value} / {item.max}</span>
                    </div>
                    <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                      <div 
                        className="h-full rounded-full transition-all"
                        style={{ width: `${(item.value / item.max) * 100}%`, background: item.color }}
                      />
                    </div>
                  </div>
                ))}
              </div>

              {/* Key Metrics */}
              <div className="col-span-4 grid grid-cols-2 gap-3">
                <div className="bg-gray-50 rounded-xl p-3">
                  <DollarSign className="w-5 h-5 text-green-500 mb-1" />
                  <p className="text-lg font-bold text-gray-900">${esgData.metrics.total_giving.toLocaleString()}</p>
                  <p className="text-xs text-gray-500">Total Giving</p>
                </div>
                <div className="bg-gray-50 rounded-xl p-3">
                  <Users className="w-5 h-5 text-blue-500 mb-1" />
                  <p className="text-lg font-bold text-gray-900">{esgData.metrics.employee_participation_rate}%</p>
                  <p className="text-xs text-gray-500">Participation</p>
                </div>
                <div className="bg-gray-50 rounded-xl p-3">
                  <Clock className="w-5 h-5 text-purple-500 mb-1" />
                  <p className="text-lg font-bold text-gray-900">{esgData.metrics.total_volunteer_hours}</p>
                  <p className="text-xs text-gray-500">Volunteer Hrs</p>
                </div>
                <div className="bg-gray-50 rounded-xl p-3">
                  <Target className="w-5 h-5 text-amber-500 mb-1" />
                  <p className="text-lg font-bold text-gray-900">{esgData.metrics.charities_supported}</p>
                  <p className="text-xs text-gray-500">Charities</p>
                </div>
              </div>
            </div>
          </div>

          {/* Charts Row */}
          <div className="grid grid-cols-2 gap-4">
            {/* Monthly Trend */}
            <div className="bg-white rounded-xl shadow-sm border p-4">
              <h4 className="font-semibold text-sm mb-4" style={{ color: COLORS.accent }}>Monthly Giving Trend</h4>
              <ResponsiveContainer width="100%" height={200}>
                <AreaChart data={esgData.monthly_trend}>
                  <defs>
                    <linearGradient id="colorAmount" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={COLORS.primary} stopOpacity={0.3}/>
                      <stop offset="95%" stopColor={COLORS.primary} stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <XAxis dataKey="month" axisLine={false} tickLine={false} tick={{ fontSize: 10 }} />
                  <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 10 }} tickFormatter={v => `$${v/1000}k`} />
                  <Tooltip formatter={(v) => [`$${v.toLocaleString()}`, 'Amount']} />
                  <Area type="monotone" dataKey="amount" stroke={COLORS.primary} fill="url(#colorAmount)" strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            </div>

            {/* Category Breakdown */}
            <div className="bg-white rounded-xl shadow-sm border p-4">
              <h4 className="font-semibold text-sm mb-4" style={{ color: COLORS.accent }}>Giving by Category</h4>
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie
                    data={esgData.category_breakdown.slice(0, 5)}
                    dataKey="amount"
                    nameKey="category"
                    cx="50%"
                    cy="50%"
                    innerRadius={40}
                    outerRadius={70}
                    paddingAngle={2}
                  >
                    {esgData.category_breakdown.slice(0, 5).map((_, i) => (
                      <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(v) => [`$${v.toLocaleString()}`, '']} />
                </PieChart>
              </ResponsiveContainer>
              <div className="flex flex-wrap gap-2 mt-2">
                {esgData.category_breakdown.slice(0, 5).map((cat, i) => (
                  <span key={cat.category} className="flex items-center gap-1 text-xs">
                    <span className="w-2 h-2 rounded-full" style={{ background: CHART_COLORS[i % CHART_COLORS.length] }} />
                    {cat.category}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tax Receipts Report */}
      {activeReport === 'tax' && taxData && (
        <div className="space-y-4">
          {/* Summary Card */}
          <div className="bg-white rounded-xl shadow-sm border p-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="text-lg font-bold" style={{ color: COLORS.accent }}>Tax Receipt Summary - {selectedYear}</h3>
                <p className="text-sm text-gray-500">Tax-deductible donation records</p>
              </div>
              <div className="flex gap-2">
                <button
                  data-testid="download-tax-summary"
                  onClick={() => setShowTaxDocument(true)}
                  className="px-4 py-2 text-sm font-medium rounded-lg flex items-center gap-2 text-white"
                  style={{ background: COLORS.primary }}
                >
                  <FileText className="w-4 h-4" /> Download Tax Summary
                </button>
                <button
                  onClick={() => exportReport('donations')}
                  className="px-4 py-2 text-sm font-medium rounded-lg flex items-center gap-2"
                  style={{ background: `${COLORS.primary}15`, color: COLORS.primary }}
                >
                  <Download className="w-4 h-4" /> Export CSV
                </button>
              </div>
            </div>

            <div className="grid grid-cols-4 gap-4 mb-6">
              <div className="bg-green-50 rounded-xl p-4">
                <p className="text-2xl font-bold text-green-700">${taxData.total_company_giving.toLocaleString()}</p>
                <p className="text-sm text-green-600">Total Company Giving</p>
              </div>
              <div className="bg-blue-50 rounded-xl p-4">
                <p className="text-2xl font-bold text-blue-700">${taxData.total_employee_donations.toLocaleString()}</p>
                <p className="text-sm text-blue-600">Employee Donations</p>
              </div>
              <div className="bg-purple-50 rounded-xl p-4">
                <p className="text-2xl font-bold text-purple-700">${taxData.total_company_match.toLocaleString()}</p>
                <p className="text-sm text-purple-600">Company Match</p>
              </div>
              <div className="bg-amber-50 rounded-xl p-4">
                <p className="text-2xl font-bold text-amber-700">{taxData.donor_count}</p>
                <p className="text-sm text-amber-600">Total Donors</p>
              </div>
            </div>

            {/* Individual Receipts */}
            <div className="border rounded-xl overflow-hidden">
              <div className="bg-gray-50 px-4 py-3 border-b">
                <h4 className="font-semibold text-sm">Individual Donor Receipts</h4>
              </div>
              <div className="max-h-96 overflow-y-auto">
                {taxData.receipts.map((receipt, i) => (
                  <div key={i} className="px-4 py-3 border-b last:border-0 hover:bg-gray-50">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-gray-900">{receipt.donor_name}</p>
                        <p className="text-sm text-gray-500">{receipt.donor_email}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-bold" style={{ color: COLORS.primary }}>${receipt.total_donated.toLocaleString()}</p>
                        <p className="text-xs text-gray-500">{receipt.donations.length} donation(s)</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Impact Report */}
      {activeReport === 'impact' && impactData && (
        <div className="space-y-4">
          {/* Impact Summary */}
          <div className="bg-white rounded-xl shadow-sm border p-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="text-lg font-bold" style={{ color: COLORS.accent }}>Impact Report</h3>
                <p className="text-sm text-gray-500">Measuring the difference you&apos;re making</p>
              </div>
            </div>

            <div className="grid grid-cols-6 gap-4 mb-6">
              <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl p-4 text-white">
                <DollarSign className="w-6 h-6 mb-2 opacity-80" />
                <p className="text-2xl font-bold">${impactData.summary.total_raised.toLocaleString()}</p>
                <p className="text-sm opacity-90">Total Raised</p>
              </div>
              <div className="bg-gradient-to-br from-pink-500 to-pink-600 rounded-xl p-4 text-white">
                <Heart className="w-6 h-6 mb-2 opacity-80" />
                <p className="text-2xl font-bold">{impactData.summary.lives_impacted.toLocaleString()}</p>
                <p className="text-sm opacity-90">Lives Impacted</p>
              </div>
              <div className="bg-gradient-to-br from-amber-500 to-amber-600 rounded-xl p-4 text-white">
                <span className="text-2xl mb-2 block">🍽️</span>
                <p className="text-2xl font-bold">{impactData.summary.meals_provided.toLocaleString()}</p>
                <p className="text-sm opacity-90">Meals Provided</p>
              </div>
              <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl p-4 text-white">
                <Clock className="w-6 h-6 mb-2 opacity-80" />
                <p className="text-2xl font-bold">{impactData.summary.volunteer_hours.toLocaleString()}</p>
                <p className="text-sm opacity-90">Volunteer Hours</p>
              </div>
              <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-4 text-white">
                <DollarSign className="w-6 h-6 mb-2 opacity-80" />
                <p className="text-2xl font-bold">${impactData.summary.volunteer_value.toLocaleString()}</p>
                <p className="text-sm opacity-90">Volunteer Value</p>
              </div>
              <div className="bg-gradient-to-br from-cyan-500 to-cyan-600 rounded-xl p-4 text-white">
                <Target className="w-6 h-6 mb-2 opacity-80" />
                <p className="text-2xl font-bold">{impactData.summary.charities_supported}</p>
                <p className="text-sm opacity-90">Charities</p>
              </div>
            </div>

            {/* Top Campaigns */}
            <div>
              <h4 className="font-semibold text-sm mb-3" style={{ color: COLORS.accent }}>Top Performing Campaigns</h4>
              <div className="grid grid-cols-2 gap-3">
                {impactData.top_campaigns.map((campaign, i) => (
                  <div key={i} className="bg-gray-50 rounded-xl p-4">
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <p className="font-semibold text-gray-900">{campaign.title}</p>
                        <p className="text-sm text-gray-500">{campaign.charity_name}</p>
                      </div>
                      <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs font-medium rounded-full">
                        {((campaign.raised / campaign.goal) * 100).toFixed(0)}%
                      </span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-600">${campaign.raised.toLocaleString()} raised</span>
                      {campaign.impact_metric && (
                        <span className="text-purple-600 font-medium">{campaign.impact_count} {campaign.impact_metric}</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Matching Report */}
      {activeReport === 'matching' && (
        <div className="space-y-4">
          {/* Summary Cards */}
          <div className="grid grid-cols-4 gap-4">
            <div className="bg-white rounded-xl shadow-sm border p-4">
              <div className="flex items-center gap-2 mb-2">
                <DollarSign className="w-5 h-5 text-green-500" />
                <span className="text-sm text-gray-500">Employee Donations</span>
              </div>
              <p className="text-2xl font-bold text-gray-900">
                ${(matchingData?.summary?.total_employee_donations || 0).toLocaleString()}
              </p>
            </div>
            <div className="bg-white rounded-xl shadow-sm border p-4">
              <div className="flex items-center gap-2 mb-2">
                <Target className="w-5 h-5 text-blue-500" />
                <span className="text-sm text-gray-500">Match Owed</span>
              </div>
              <p className="text-2xl font-bold text-blue-600">
                ${(matchingData?.summary?.total_match_owed || 0).toLocaleString()}
              </p>
            </div>
            <div className="bg-white rounded-xl shadow-sm border p-4">
              <div className="flex items-center gap-2 mb-2">
                <CheckCircle className="w-5 h-5 text-green-500" />
                <span className="text-sm text-gray-500">Paid</span>
              </div>
              <p className="text-2xl font-bold text-green-600">
                ${(matchingData?.summary?.total_paid || 0).toLocaleString()}
              </p>
            </div>
            <div className="bg-white rounded-xl shadow-sm border p-4">
              <div className="flex items-center gap-2 mb-2">
                <Clock className="w-5 h-5 text-orange-500" />
                <span className="text-sm text-gray-500">Outstanding</span>
              </div>
              <p className="text-2xl font-bold text-orange-600">
                ${(matchingData?.summary?.total_outstanding || 0).toLocaleString()}
              </p>
            </div>
          </div>

          {/* Charity Breakdown Table */}
          <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
            <div className="px-4 py-3 border-b bg-gray-50 flex items-center justify-between">
              <div>
                <h3 className="font-semibold text-gray-900">Charity Matching Breakdown</h3>
                <p className="text-xs text-gray-500">
                  Match Ratio: {matchingData?.business_settings?.match_ratio || 1}:1 | 
                  Cap per Employee: ${(matchingData?.business_settings?.match_cap_per_employee || 0).toLocaleString()}
                </p>
              </div>
              <button
                onClick={() => exportReport('matching')}
                className="px-3 py-1.5 text-sm font-medium rounded-lg flex items-center gap-1.5"
                style={{ background: `${COLORS.primary}15`, color: COLORS.primary }}
              >
                <Download className="w-4 h-4" /> Export
              </button>
            </div>

            {matchingData?.charities?.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b bg-gray-50">
                      <th className="text-left px-4 py-3 text-xs font-medium text-gray-500">Charity</th>
                      <th className="text-right px-4 py-3 text-xs font-medium text-gray-500">Employee Donations</th>
                      <th className="text-right px-4 py-3 text-xs font-medium text-gray-500">Match Owed</th>
                      <th className="text-right px-4 py-3 text-xs font-medium text-gray-500">Paid</th>
                      <th className="text-right px-4 py-3 text-xs font-medium text-gray-500">Outstanding</th>
                      <th className="text-center px-4 py-3 text-xs font-medium text-gray-500">Donors</th>
                      <th className="text-right px-4 py-3 text-xs font-medium text-gray-500">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {matchingData.charities.map((charity, i) => (
                      <tr key={i} className="border-b hover:bg-gray-50">
                        <td className="px-4 py-3">
                          <p className="font-medium text-gray-900">{charity.charity_name}</p>
                          {charity.charity_ein && (
                            <p className="text-xs text-gray-500">EIN: {charity.charity_ein}</p>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right font-medium text-gray-900">
                          ${charity.employee_donations.toLocaleString()}
                        </td>
                        <td className="px-4 py-3 text-right font-medium text-blue-600">
                          ${charity.match_amount_owed.toLocaleString()}
                        </td>
                        <td className="px-4 py-3 text-right font-medium text-green-600">
                          ${charity.paid_amount.toLocaleString()}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <span className={`font-bold ${charity.outstanding_amount > 0 ? 'text-orange-600' : 'text-green-600'}`}>
                            ${charity.outstanding_amount.toLocaleString()}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-center text-sm text-gray-600">
                          {charity.donors}
                        </td>
                        <td className="px-4 py-3 text-right">
                          {charity.outstanding_amount > 0 && (
                            <button
                              onClick={() => {
                                setSelectedCharity(charity);
                                setPaymentAmount(charity.outstanding_amount.toString());
                                setShowPaymentModal(true);
                              }}
                              className="px-3 py-1.5 text-xs font-medium rounded-lg text-white"
                              style={{ background: COLORS.primary }}
                            >
                              Record Payment
                            </button>
                          )}
                          {charity.outstanding_amount === 0 && (
                            <span className="px-2 py-1 bg-green-100 text-green-700 text-xs font-medium rounded-full">
                              Paid
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="p-8 text-center text-gray-500">
                <DollarSign className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                <p className="font-medium">No matching donations yet</p>
                <p className="text-sm">When employees make matched donations, they&apos;ll appear here.</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Payment Modal */}
      {showPaymentModal && selectedCharity && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md m-4 p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Record Matching Payment</h3>
            
            <div className="space-y-4">
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-sm text-gray-500">Paying to</p>
                <p className="font-semibold text-gray-900">{selectedCharity.charity_name}</p>
                <p className="text-sm text-gray-500">Outstanding: ${selectedCharity.outstanding_amount.toLocaleString()}</p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Payment Amount</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                  <input
                    type="number"
                    value={paymentAmount}
                    onChange={(e) => setPaymentAmount(e.target.value)}
                    className="w-full pl-8 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="0.00"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Payment Method</label>
                <select
                  value={paymentMethod}
                  onChange={(e) => setPaymentMethod(e.target.value)}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="check">Check</option>
                  <option value="wire">Wire Transfer</option>
                  <option value="ach">ACH</option>
                  <option value="card">Credit Card</option>
                  <option value="other">Other</option>
                </select>
              </div>
            </div>
            
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => {
                  setShowPaymentModal(false);
                  setSelectedCharity(null);
                  setPaymentAmount('');
                }}
                className="flex-1 px-4 py-2 border rounded-lg font-medium text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleRecordPayment}
                disabled={processingPayment || !paymentAmount || parseFloat(paymentAmount) <= 0}
                className="flex-1 px-4 py-2 rounded-lg font-medium text-white disabled:opacity-50"
                style={{ background: COLORS.primary }}
              >
                {processingPayment ? 'Recording...' : 'Record Payment'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Tax Summary Document Modal */}
      {showTaxDocument && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4" onMouseDown={(e) => e.target === e.currentTarget && setShowTaxDocument(false)}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-auto" data-testid="tax-document-modal">
            <div id="tax-summary-content" className="p-8">
              <div className="flex justify-between items-start mb-8 border-b-2 border-gray-900 pb-6">
                <div>
                  <h1 className="text-2xl font-bold text-gray-900">Charitable Giving Tax Summary</h1>
                  <p className="text-gray-500 mt-1">Tax Year {selectedYear}</p>
                </div>
                <div className="text-right">
                  <p className="text-sm font-semibold text-gray-900">{taxData?.business_name || 'Company'}</p>
                  <p className="text-xs text-gray-500">{taxData?.business_ein || ''}</p>
                  <p className="text-xs text-gray-500 mt-1">Generated {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4 mb-8 p-4 bg-gray-50 rounded-xl">
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wide font-medium">Total Tax-Deductible Donations</p>
                  <p className="text-2xl font-bold text-gray-900 mt-1">
                    ${(taxData?.receipts || []).reduce((sum, r) => sum + (r.total_amount || 0), 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wide font-medium">Number of Donations</p>
                  <p className="text-2xl font-bold text-gray-900 mt-1">
                    {(taxData?.receipts || []).reduce((sum, r) => sum + (r.donations?.length || 0), 0)}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wide font-medium">Charities Supported</p>
                  <p className="text-2xl font-bold text-gray-900 mt-1">
                    {(taxData?.receipts || []).length}
                  </p>
                </div>
              </div>

              <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wide mb-4">Itemized Donations by Charity</h3>
              
              {(taxData?.receipts || []).length === 0 ? (
                <div className="text-center py-8 text-gray-400">
                  <FileText className="w-8 h-8 mx-auto mb-2" />
                  <p>No tax-deductible donations recorded for {selectedYear}</p>
                </div>
              ) : (
                <table className="w-full text-sm mb-8">
                  <thead>
                    <tr className="border-b-2 border-gray-300">
                      <th className="text-left py-2 font-semibold text-gray-700">Charity / Organization</th>
                      <th className="text-left py-2 font-semibold text-gray-700">EIN / Tax ID</th>
                      <th className="text-right py-2 font-semibold text-gray-700">Donations</th>
                      <th className="text-right py-2 font-semibold text-gray-700">Total Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(taxData?.receipts || []).map((receipt, i) => (
                      <tr key={i} className="border-b border-gray-100">
                        <td className="py-3 text-gray-900 font-medium">{receipt.charity_name}</td>
                        <td className="py-3 text-gray-600">{receipt.charity_ein || 'N/A'}</td>
                        <td className="py-3 text-right text-gray-600">{receipt.donations?.length || 0}</td>
                        <td className="py-3 text-right font-semibold text-gray-900">${(receipt.total_amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="border-t-2 border-gray-900">
                      <td colSpan="3" className="py-3 text-right font-bold text-gray-900 uppercase text-xs tracking-wide">Total Tax-Deductible Giving</td>
                      <td className="py-3 text-right font-bold text-lg text-gray-900">
                        ${(taxData?.receipts || []).reduce((sum, r) => sum + (r.total_amount || 0), 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              )}

              {(taxData?.receipts || []).some(r => r.donations?.length > 0) && (
                <>
                  <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wide mb-4">Detailed Transaction Log</h3>
                  <table className="w-full text-xs mb-6">
                    <thead>
                      <tr className="border-b-2 border-gray-300">
                        <th className="text-left py-2 font-semibold text-gray-700">Date</th>
                        <th className="text-left py-2 font-semibold text-gray-700">Charity</th>
                        <th className="text-left py-2 font-semibold text-gray-700">Donor</th>
                        <th className="text-left py-2 font-semibold text-gray-700">Type</th>
                        <th className="text-right py-2 font-semibold text-gray-700">Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(taxData?.receipts || []).flatMap(receipt =>
                        (receipt.donations || []).map((d, di) => (
                          <tr key={`${receipt.charity_name}-${di}`} className="border-b border-gray-50">
                            <td className="py-2 text-gray-600">{new Date(d.date || d.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</td>
                            <td className="py-2 text-gray-900">{receipt.charity_name}</td>
                            <td className="py-2 text-gray-600">{d.donor_name || d.donor_email || 'Anonymous'}</td>
                            <td className="py-2 text-gray-600">{d.type || 'Donation'}</td>
                            <td className="py-2 text-right font-medium text-gray-900">${(d.amount || 0).toFixed(2)}</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </>
              )}

              <div className="mt-6 pt-4 border-t border-gray-200">
                <p className="text-[10px] text-gray-400 leading-relaxed">
                  This document is provided for informational purposes only. All donations were processed through Every.org, 
                  a 501(c)(3) public charity (EIN: 61-1913823). Individual donation receipts are issued by Every.org at the time of donation. 
                  Please consult with your tax advisor regarding the deductibility of charitable contributions. 
                  This summary does not constitute tax advice.
                </p>
              </div>
            </div>

            <div className="flex items-center justify-between px-8 py-4 border-t bg-gray-50 rounded-b-2xl">
              <button onClick={() => setShowTaxDocument(false)} className="text-sm text-gray-500 hover:text-gray-700">Close</button>
              <button
                data-testid="print-tax-summary"
                onClick={() => {
                  const content = document.getElementById('tax-summary-content');
                  const printWindow = window.open('', '_blank');
                  printWindow.document.write(`
                    <html><head><title>Tax Summary ${selectedYear}</title>
                    <style>
                      body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; padding: 40px; color: #111; }
                      table { width: 100%; border-collapse: collapse; }
                      th, td { padding: 8px 12px; text-align: left; }
                      th { font-weight: 600; }
                      .text-right { text-align: right; }
                      @media print { body { padding: 20px; } }
                    </style></head>
                    <body>${content.innerHTML}</body></html>
                  `);
                  printWindow.document.close();
                  printWindow.print();
                }}
                className="px-5 py-2.5 text-white text-sm font-semibold rounded-lg flex items-center gap-2"
                style={{ background: COLORS.primary }}
              >
                <Download className="w-4 h-4" /> Print / Save as PDF
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReportsView;
